/**
 * Utilidades para manejo de archivos
 */

const API_BASE_URL = process.env.REACT_APP_API_ROOT_URL
  || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : '');

/**
 * Función para verificar la disponibilidad de la plantilla de receta
 * @returns {Promise<boolean>} Verdadero si la plantilla está disponible
 */
export const verificarPlantillaReceta = async () => {
  console.log('Verificando disponibilidad de la plantilla de receta...');
  
  const posiblesRutas = [
    '/data/formato_receta.pdf',
    `${process.env.PUBLIC_URL}/data/formato_receta.pdf`,
    '../data/formato_receta.pdf'
  ];
  
  for (const ruta of posiblesRutas) {
    try {
      console.log(`Intentando acceder a: ${ruta}`);
      const response = await fetch(ruta, { method: 'HEAD' });
      
      if (response.ok) {
        console.log(`✅ Plantilla encontrada en: ${ruta}`);
        return { disponible: true, ruta };
      }
    } catch (error) {
      console.warn(`No se pudo acceder a la plantilla en: ${ruta}`, error.message);
    }
  }
  
  console.error('❌ No se encontró la plantilla en ninguna ubicación');
  return { disponible: false, ruta: null };
};

/**
 * Función para copiar la plantilla de receta al directorio public
 * Esta función debe ejecutarse al inicio de la aplicación
 * @returns {Promise<boolean>} Verdadero si la operación fue exitosa
 */
export const copiarPlantillaReceta = async () => {
  try {
    // Primero verificamos si la plantilla ya está disponible
    const verificacion = await verificarPlantillaReceta();
    if (verificacion.disponible) {
      console.log('La plantilla ya está disponible en:', verificacion.ruta);
      return true;
    }
    
    // Si no está disponible, intentamos copiarla
    console.log('Intentando copiar la plantilla de receta...');
    
    // En un entorno de desarrollo, mostramos mensaje informativo
    if (process.env.NODE_ENV === 'development') {
      console.log(`
=======================================================
⚠️ ATENCIÓN: Plantilla de receta no encontrada
-------------------------------------------------------
Para que las recetas funcionen correctamente, copie 
manualmente el archivo formato_receta.pdf a:

    /public/data/formato_receta.pdf

Desde la ubicación:

    /src/data/formato_receta.pdf
=======================================================
      `);
    }
    
    // Nota: En un entorno de producción real, se necesitaría un endpoint
    // en el backend para manejar esta copia de archivos
    
    return false;
  } catch (error) {
    console.error('Error al copiar la plantilla de receta:', error);
    return false;
  }
};

/**
 * Función para determinar la URL correcta para acceder a la plantilla
 * @returns {Promise<string|null>} URL de la plantilla o null si no está disponible
 */
export const obtenerUrlPlantilla = async () => {
  const verificacion = await verificarPlantillaReceta();
  return verificacion.disponible ? verificacion.ruta : null;
};

/**
 * Función para verificar la disponibilidad de una plantilla personalizada
 * @param {string} rutaPlantilla - Ruta a la plantilla personalizada
 * @returns {Promise<boolean>} Verdadero si la plantilla está disponible
 */
export const verificarPlantillaPersonalizada = async (rutaPlantilla) => {
  if (!rutaPlantilla) return false;
  
  console.log('Verificando disponibilidad de plantilla personalizada:', rutaPlantilla);
  
  try {
    // Intentar acceder directamente a la ruta
    const response = await fetch(rutaPlantilla, { method: 'HEAD' });
    
    if (response.ok) {
      console.log(`✅ Plantilla personalizada encontrada en: ${rutaPlantilla}`);
      return true;
    }
    
    // Si no se encuentra, intentar con la URL base del backend
    const urlCompleta = API_BASE_URL ? `${API_BASE_URL}${rutaPlantilla}` : rutaPlantilla;
    
    console.log(`Intentando acceder a: ${urlCompleta}`);
    const responseApi = await fetch(urlCompleta, { method: 'HEAD' });
    
    if (responseApi.ok) {
      console.log(`✅ Plantilla personalizada encontrada en API: ${urlCompleta}`);
      return true;
    }
  } catch (error) {
    console.warn(`No se pudo acceder a la plantilla personalizada: ${rutaPlantilla}`, error.message);
  }
  
  console.error('❌ No se encontró la plantilla personalizada');
  return false;
};

/**
 * Función para obtener la URL completa de una plantilla personalizada
 * @param {string} rutaPlantilla - Ruta a la plantilla personalizada
 * @returns {Promise<string|null>} URL completa de la plantilla o null si no está disponible
 */
export const obtenerUrlPlantillaPersonalizada = async (rutaPlantilla) => {
  if (!rutaPlantilla) return null;
  
  // Verificar si la plantilla está disponible directamente
  try {
    const response = await fetch(rutaPlantilla, { method: 'HEAD' });
    if (response.ok) return rutaPlantilla;
  } catch (e) {
    // Continuar con otros intentos si falla
  }
  
  // Intentar con la URL base del backend
  const urlCompleta = API_BASE_URL ? `${API_BASE_URL}${rutaPlantilla}` : rutaPlantilla;
  
  try {
    const responseApi = await fetch(urlCompleta, { method: 'HEAD' });
    if (responseApi.ok) return urlCompleta;
  } catch (e) {
    // Si falla, intentar con el endpoint específico
  }
  
  // Intentar con el endpoint específico para plantillas
  const endpointUrl = API_BASE_URL
    ? `${API_BASE_URL}/api/doctores/plantilla-receta-pdf`
    : '/api/doctores/plantilla-receta-pdf';
  
  try {
    // Incluir token de autenticación si está disponible
    const headers = {};
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const responseEndpoint = await fetch(endpointUrl, { 
      method: 'HEAD',
      headers
    });
    
    if (responseEndpoint.ok) return endpointUrl;
  } catch (e) {
    // Si todos los intentos fallan, retornar null
  }
  
  return null;
};
